import { computed, unref } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { store } from '@/store'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

import apis from '@/services'

function createBookingDataHook(queryKeyPrefix, serviceMethod, computeValues) {
  return function (accommodationId, currency, startDate, endDate, marketCode, selectedStatus) {
    const isQueryEnabled = computed(
      () =>
        !!accommodationId?.value &&
        !!currency?.value &&
        !!startDate?.value &&
        !!endDate?.value &&
        !!marketCode.value &&
        !!selectedStatus.value
    )

    const { data, isFetching, isSuccess, isError, isLoading } = useQuery({
      queryKey: [queryKeyPrefix, accommodationId, currency, startDate, endDate, marketCode, selectedStatus],
      enabled: isQueryEnabled,
      queryFn: () =>
        serviceMethod({
          accommodationId: accommodationId.value,
          currencyCode: currency.value,
          startDate: startDate.value,
          endDate: endDate.value,
          marketCodes: marketCode.value === 'all' ? '' : marketCode.value,
          status: selectedStatus.value?.join(','),
        }),
    })

    const isInitialLoading = computed(() => isLoading.value && isFetching.value)
    const customComputedValues = computeValues ? computeValues(data) : {}

    return {
      isFetching,
      isSuccess,
      isError,
      isInitialLoading,
      ...customComputedValues,
    }
  }
}

export function useActiveMarkets() {
  const { isLoading, data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['RCActiveMarkets', selectedAccommodationId],
    queryFn: ({ queryKey }) => apis.rateConnect.getActiveMarkets({ accommodationId: queryKey[1] }),
  })

  const activeMarkets = computed(() => data?.value?.data)

  return {
    isLoading,
    activeMarkets,
    isFetching,
    isSuccess,
    isError,
  }
}

export function useCommissionLevels() {
  const { isLoading, data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['RCCommissionLevels'],
    queryFn: () => apis.rateConnect.getCommissionLevels(),
  })

  const commissionLevels = computed(() => data?.value?.data)

  const eligibleCommissionLevels = computed(() => {
    return data?.value?.data.map(item => item.commissionLevel) ?? []
  })

  return {
    isLoading,
    commissionLevels,
    eligibleCommissionLevels,
    isFetching,
    isSuccess,
    isError,
  }
}

export function useExpectedTopShare(currentCpa) {
  const { isLoading, data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['RCExpectedTopShare', currentCpa],
    queryFn: ({ queryKey }) => apis.rateConnect.getExpectedTopShare({ currentCpa: queryKey[1] }),
  })

  const expectedTopShare = computed(() => data?.value?.data)
  return {
    isLoading,
    expectedTopShare,
    isFetching,
    isSuccess,
    isError,
  }
}

export function useCompetitivenessStats(accommodationId) {
  const { isLoading, data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['RCCompetitivenessStats', accommodationId],
    queryFn: ({ queryKey }) => apis.rateConnect.getCompetitivenessStats({ accommodationId: queryKey[1] }),
  })

  const competitivenessStats = computed(() => data?.value?.data)

  return {
    isLoading,
    competitivenessStats,
    isFetching,
    isSuccess,
    isError,
  }
}

export function useBoostOpportunities(accommodationId) {
  const isQueryEnabled = computed(() => !!unref(accommodationId))

  const { isLoading, data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['RCBoostOpportunities', accommodationId],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) => apis.rateConnect.getBoostOpportunities({ accommodationId: queryKey[1] }),
  })

  const boostOpportunitiesData = computed(() => data?.value?.data || {})
  const boostOpportunitiesChartData = computed(() => boostOpportunitiesData.value?.chartData || [])
  const boostOpportunitiesCards = computed(() => boostOpportunitiesData.value?.cards || [])

  return {
    isLoading,
    boostOpportunitiesChartData,
    boostOpportunitiesCards,
    isFetching,
    isSuccess,
    isError,
  }
}

export function useCampaign(accommodationId) {
  const isQueryEnabled = computed(() => !!unref(accommodationId))
  const { isLoading, data, isFetching, isSuccess, isError, fetchStatus } = useQuery({
    queryKey: ['RCCampaigns', accommodationId],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) => apis.rateConnect.fetchCampaign({ accommodationId: queryKey[1] }),
  })

  const campaignsData = computed(() => data?.value?.data || null)

  const currentCommissionLevel = computed(() => campaignsData?.value?.cpa)
  const isUsingAutomatedMarkets = computed(() => campaignsData.value?.useAutomatedMarkets)
  const currentPartner = computed(() => campaignsData.value?.partnerId)
  const currentCampaignType = computed(() =>
    campaignsData.value?.campaignType?.toLowerCase() === 'netcpa'
      ? 'netcpa'
      : campaignsData.value?.campaignType?.toLowerCase()
  )
  const isCampaignsDataInitialLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isLoading,
    isFetching,
    isCampaignsDataInitialLoading,
    isSuccess,
    isError,
    currentCommissionLevel,
    isUsingAutomatedMarkets,
    currentCampaignType,
    currentPartner,
    campaignsData,
  }
}

const getPartners = async ({ accommodationId }) => {
  const response = await apis.rateConnect.fetchPartners({ accommodationId })
  return response
}

export function usePartners(accommodationId) {
  const isQueryEnabled = computed(() => !!unref(accommodationId))
  const { isLoading, data, isFetching, isError } = useQuery({
    queryKey: ['RCPartners', accommodationId],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) => getPartners({ accommodationId: queryKey[1] }),
  })

  const partnersData = computed(() => data.value)
  const activePartnersData = computed(() =>
    data.value?.filter(item => item.partnerStatus === 5 && item.connectionStatus === 5)
  )
  const isNetCpaEligible = computed(() => {
    const netCpaPartners = activePartnersData.value?.find(item => item.canNetCpa)
    return !!netCpaPartners
  })

  const partnersOptions = computed(() => {
    return activePartnersData.value
      ?.filter(item => item.canNetCpa)
      .map(partnerItem => {
        return {
          value: partnerItem.partnerId,
          displayText: partnerItem.partnerName,
        }
      })
  })

  return {
    isLoading,
    partnersData,
    activePartnersData,
    partnersOptions,
    isNetCpaEligible,
    isFetching,
    isError,
  }
}

const getAllPartners = async () => {
  const response = await apis.rateConnect.fetchAllPartners()
  return response
}

export function useAllPartners() {
  const { isLoading, isError, data, isFetching } = useQuery({
    queryKey: ['RCAllPartners'],
    queryFn: () => getAllPartners(),
  })

  const allPartnersData = computed(() => data?.value)
  return { isLoading, allPartnersData, isError, isFetching }
}

export function useBudget({ accommodationId, campaignType }) {
  const isEnabled = computed(() => campaignType.value === 'cpc')
  const { isFetching, isLoading, isError, data, fetchStatus } = useQuery({
    queryKey: ['RCBudget', accommodationId],
    enabled: !!isEnabled.value,
    queryFn: ({ queryKey }) => apis.rateConnect.getAccommodationBudget(queryKey[1]),
  })

  const budget = computed(() => data?.value?.data)
  const isBudgetInitalLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')
  return { isLoading, budget, isError, isFetching, isBudgetInitalLoading }
}
export function useBudgetRecommendation(currencyCode, partner) {
  const isQueryEnabled = computed(() => {
    return !!selectedAccommodationId?.value && !!currencyCode?.value && !!partner?.value
  })

  const { isLoading, isFetching, data, isSuccess, isError } = useQuery({
    queryKey: [
      'RCBudgetRecommendation',
      selectedAccommodationId?.value,
      currencyCode?.value,
      partner?.value?.partnerId,
    ],
    enabled: isQueryEnabled,
    cacheTime: 0,
    staleTime: 0,
    queryFn: ({ queryKey }) =>
      apis.rateConnect.fetchRecommendation({
        accommodationId: queryKey[1],
        currencyCode: queryKey[2],
        partnerId: partner?.value,
      }),
  })

  const isInitialLoading = computed(() => isLoading.value && isFetching.value)
  const budgetRecommendationData = computed(() => data?.value?.data)
  const budgetRecommendedMin = computed(() =>
    budgetRecommendationData.value?.budgetRecommendedMin
      ? Math.round(budgetRecommendationData.value?.budgetRecommendedMin / 100)
      : 0
  )
  const budgetRecommendedMax = computed(() =>
    budgetRecommendationData.value?.budgetRecommendedMax
      ? Math.round(budgetRecommendationData.value?.budgetRecommendedMax / 100)
      : 0
  )
  const minBudget = computed(() =>
    budgetRecommendationData.value?.budgetMin ? Math.round(budgetRecommendationData.value.budgetMin / 100) : 0
  )
  const maxBudget = computed(() => (budgetRecommendedMax.value ? Math.round(budgetRecommendedMax.value * 1.33) : 0))
  const defaultBudget = computed(() => {
    if (!budgetRecommendedMin.value || !budgetRecommendedMax.value) return 0
    return Math.round((budgetRecommendedMin.value + budgetRecommendedMax.value) / 2)
  })

  const isBudgetValid = selectedBudget => selectedBudget >= minBudget.value

  return {
    isFetching,
    isSuccess,
    isError,
    isInitialLoading,
    budgetRecommendationData,
    minBudget,
    maxBudget,
    budgetRecommendedMin,
    budgetRecommendedMax,
    defaultBudget,
    isBudgetValid,
  }
}

const getMarkets = async ({ accommodationId, partnerId }) => {
  const response = await apis.rateConnect.fetchMarkets({ accommodationId, partnerId })
  return response
}

export function usePartnerMarkets(accommodationId, language, currentPartner) {
  const isQueryEnabled = computed(() => !!accommodationId?.value && !!language?.value && !!currentPartner?.value)

  const { data, isFetching, isSuccess, isError, isLoading } = useQuery({
    queryKey: ['RCMarkets', accommodationId?.value, language?.value, currentPartner.value],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) =>
      getMarkets({
        accommodationId: queryKey[1],
        partnerId: currentPartner.value,
      }),
  })

  const partnerMarkets = computed(() => data?.value?.data)
  const isInitialLoading = computed(() => isLoading.value && isFetching.value)
  const marketsWithCampaign = computed(() => partnerMarkets.value?.filter(market => market.hasCampaign))

  return {
    isFetching,
    isSuccess,
    isError,
    isInitialLoading,
    partnerMarkets,
    marketsWithCampaign,
  }
}

export const useCheckinData = createBookingDataHook(
  'RCCheckinData',
  params => apis.rateConnect.fetchCheckinData(params),
  data => ({
    checkinDataAmount: computed(() => data?.value?.data?.bookings?.length),
  })
)

export const usePrevCheckinData = createBookingDataHook(
  'RCPrevCheckinData',
  params => apis.rateConnect.fetchCheckinData(params),
  data => ({
    prevCheckinDataAmount: computed(() => data?.value?.data?.bookings?.length),
  })
)

export const useCheckoutData = createBookingDataHook(
  'RCCheckoutData',
  params => apis.rateConnect.fetchCheckoutData(params),
  data => ({
    checkoutDataAmount: computed(() => data?.value?.data?.bookings?.length),
    checkoutCommissionsAmount: computed(() => {
      return data?.value?.data?.bookings?.reduce((sum, item) => {
        return sum + item.commissionCost
      }, 0)
    }),
  })
)

export const usePrevCheckoutData = createBookingDataHook(
  'RCPrevCheckoutData',
  params => apis.rateConnect.fetchCheckoutData(params),
  data => ({
    prevCheckoutDataAmount: computed(() => data?.value?.data?.bookings?.length),
    prevCheckoutCommissionsAmount: computed(() =>
      data?.value?.data?.bookings?.reduce((sum, item) => sum + item.commissionCost, 0)
    ),
  })
)

export const useBookingsAmount = createBookingDataHook(
  'RCBookingsAmount',
  params => apis.rateConnect.fetchBookingsAmount(params),
  data => ({
    bookingsAmount: computed(() => data?.value?.data?.bookings?.length),
  })
)

export const usePrevBookingsAmount = createBookingDataHook(
  'RCPrevBookingsAmount',
  params => apis.rateConnect.fetchBookingsAmount(params),
  data => ({
    prevBookingsAmount: computed(() => data?.value?.data?.bookings?.length),
  })
)

export function useBookingLog(
  accommodationId,
  filtrationDate,
  currency,
  startDate,
  endDate,
  marketCode,
  selectedStatus
) {
  const isQueryEnabled = computed(
    () =>
      !!accommodationId?.value &&
      !!filtrationDate?.value &&
      !!currency?.value &&
      !!startDate?.value &&
      !!endDate?.value &&
      !!marketCode.value &&
      !!selectedStatus.value
  )

  const { data, isFetching, isSuccess, isError, isLoading } = useQuery({
    queryKey: [
      'RCBookingLog',
      accommodationId,
      filtrationDate,
      currency,
      startDate,
      endDate,
      marketCode,
      selectedStatus,
    ],
    enabled: isQueryEnabled,
    cacheTime: 0,
    queryFn: () =>
      apis.rateConnect.fetchBookingLog({
        accommodationId: accommodationId.value,
        filtrationDate: filtrationDate.value,
        currencyCode: currency.value,
        startDate: startDate.value,
        endDate: endDate.value,
        marketCodes: marketCode.value === 'all' ? '' : marketCode.value,
        status: selectedStatus.value?.join(',') || '',
      }),
  })

  const bookingLogData = computed(() => data.value?.data?.bookings || [])
  const hasBookings = computed(() => data.value?.data?.hasMoreBookings || false)
  const isInitialLoading = computed(() => isLoading.value && isFetching.value)

  return {
    isFetching,
    isSuccess,
    isError,
    isInitialLoading,
    bookingLogData,
    hasBookings,
  }
}

export function useKpis(currency, startDate, endDate, marketCode) {
  const isQueryEnabled = computed(
    () =>
      !!selectedAccommodationId?.value &&
      !!currency?.value &&
      !!startDate?.value &&
      !!endDate?.value &&
      !!marketCode.value
  )

  const { data, isFetching, isSuccess, isError, isLoading } = useQuery({
    queryKey: ['RCKpis', selectedAccommodationId, currency, startDate, endDate, marketCode],
    enabled: isQueryEnabled,
    queryFn: () =>
      apis.rateConnect.fetchKPIS({
        accommodationId: selectedAccommodationId.value,
        currencyCode: currency.value,
        startDate: startDate.value,
        endDate: endDate.value,
        marketCodes: marketCode.value === 'all' ? '' : marketCode.value,
      }),
  })

  const KpisData = computed(() => data?.value?.data)

  const kpiNamesMap = {
    visits: 'rc_clicks',
    bookings: 'rc_bookings',
    revenue: 'rc_revenue',
    cpa: 'rc_cpa',
    commissionCost: 'rc_commissions',
  }

  const prettyKpis = computed(() => {
    if (!KpisData?.value || Array.isArray(KpisData.value)) return
    return Object.keys(kpiNamesMap).map(kpiKey => ({
      key: kpiKey,
      label: kpiNamesMap[kpiKey],
      number: KpisData.value[kpiKey],
      progression: KpisData.value[`${kpiKey}Progression`],
    }))
  })

  const isInitialLoading = computed(() => isLoading.value && isFetching.value)

  return {
    isFetching,
    isSuccess,
    isError,
    isInitialLoading,
    KpisData,
    prettyKpis,
  }
}

export function useStats(currency, startDate, endDate, marketCode, aggregation) {
  const isQueryEnabled = computed(
    () =>
      !!selectedAccommodationId?.value &&
      !!currency?.value &&
      !!startDate?.value &&
      !!endDate?.value &&
      !!marketCode?.value &&
      !!aggregation?.value
  )

  const { data, isFetching, isSuccess, isError, isLoading } = useQuery({
    queryKey: ['RCStats', selectedAccommodationId, currency, startDate, endDate, marketCode, aggregation],
    enabled: isQueryEnabled,
    queryFn: () =>
      apis.rateConnect.fetchStats({
        accommodationId: selectedAccommodationId.value,
        currencyCode: currency.value,
        startDate: startDate.value,
        endDate: endDate.value,
        marketCodes: marketCode.value === 'all' ? null : marketCode.value,
        aggregation: aggregation.value,
      }),
  })

  const hasStatistics = computed(() => data?.value?.data?.hasMoreStatistics)
  const statsData = computed(() => data?.value?.data?.statistics)
  const periodSummary = computed(() => data?.value?.data?.periodSummary)
  const isInitialLoading = computed(() => isLoading.value && isFetching.value)

  return {
    isFetching,
    isSuccess,
    isError,
    isInitialLoading,
    statsData,
    hasStatistics,
    periodSummary,
  }
}

export function useNotifications(scope) {
  const isQueryEnabled = computed(() => !!unref(selectedAccommodationId) && !!unref(scope))

  const { data, isLoading, isFetching, isSuccess } = useQuery({
    queryKey: ['Notifications', selectedAccommodationId, scope],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) =>
      apis.rateConnect.fetchNotifications({ accommodationId: queryKey[1], scope: queryKey[2] }),
  })

  const notificationsData = computed(() => data?.value?.data)

  return { isLoading, notificationsData, isFetching, isSuccess }
}

export function useCampaignMutation() {
  const queryClient = useQueryClient()
  const token = computed(() => store.state.session?.studioToken)
  const { userRole } = useSessionValues()
  const guardSupportMutation =
    mutate =>
    (...args) => {
      if (userRole.value === 'support') return

      mutate(...args)
    }

  const {
    isLoading: isUpdatingCampaignType,
    isSuccess: isUpdatingCampaignTypeSuccess,
    isError: isUpdatingCampaignTypeError,
    mutate: updateCampaignTypeMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, body }) => apis.rateConnect.updateCampaignType({ accommodationId, body }),
    onSuccess: (_, mutationInput) => {
      queryClient.setQueryData(['RCCampaigns', mutationInput.accommodationId], oldData => {
        if (!oldData) return oldData
        return {
          ...oldData,
          data: {
            ...oldData.data,
            campaignType: 'netCpa',
            cpa: mutationInput?.body?.commissionLevel,
          },
        }
      })
      queryClient.invalidateQueries({ queryKey: ['RCPartners'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
    },
  })

  const {
    isLoading: isUpdatingCommissionLevel,
    isSuccess: isUpdatingCommissionLevelSuccess,
    isError: isUpdatingCommissionLevelError,
    mutate: updateCommissionLevelMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, commissionLevel }) =>
      apis.rateConnect.updateCommissionLevel({ accommodationId, commissionLevel }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['RCExpectedTopShare'] })
      queryClient.invalidateQueries({ queryKey: ['RCBoostOpportunities'] })
      queryClient.invalidateQueries({ queryKey: ['ActiveBoosts'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
    },
  })

  const {
    isLoading: isPausingCampaign,
    isSuccess: isPausingCampaignSuccess,
    isError: isPausingCampaignError,
    mutate: pauseCampaignMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, pauseUntil }) =>
      apis.rateConnect.pauseRateConnectCampaign({ accommodationId, pauseUntil }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['premiumProductsData', token] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCBudgetRecommendation'] })
    },
  })

  const {
    isLoading: isDeactivatingCampaign,
    isSuccess: isDeactivatingCampaignSuccess,
    isError: isDeactivatingCampaignError,
    mutate: deactivateCampaignMutation,
  } = useMutation({
    mutationFn: ({ accommodationId }) => apis.rateConnect.deactivateRateConnectCampaign({ accommodationId }),
    onSuccess: (_, mutationInput) => {
      queryClient.setQueryData(['RCCampaigns', mutationInput.accommodationId], oldData => {
        if (!oldData) return oldData

        return {
          ...oldData,
          data: {
            ...oldData.data,
            status: 'PAUSED',
          },
        }
      })
      queryClient.invalidateQueries({ queryKey: ['premiumProductsData', token] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCBudgetRecommendation'] })
    },
  })

  const {
    isLoading: isUpdatingCampaignBudget,
    isSuccess: isUpdatingCampaignBudgetSuccess,
    isError: isUpdatingCampaignBudgetError,
    mutate: updateCampaignBudgetMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, currencyCode, budget }) =>
      apis.rateConnect.updateCampaignBudget({ accommodationId, currencyCode, budget }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCBudgetRecommendation'] })
      queryClient.invalidateQueries({ queryKey: ['RCBudget'] })
    },
  })

  const {
    isLoading: isResumeCampaignLoading,
    isSuccess: isResumeCampaignSuccess,
    isError: isResumeCampaignError,
    mutate: resumeCampaignMutation,
  } = useMutation({
    mutationFn: ({ accommodationId }) => apis.rateConnect.resumeRateConnectCampaign({ accommodationId }),
    onSuccess: (_, mutationInput) => {
      queryClient.setQueryData(['RCCampaigns', mutationInput.accommodationId], oldData => {
        if (!oldData) return oldData
        return {
          ...oldData,
          data: {
            ...oldData.data,
            status: 'ACTIVE',
          },
        }
      })
      queryClient.invalidateQueries({ queryKey: ['premiumProductsData', token] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCBudgetRecommendation'] })
    },
  })

  const {
    isLoading: isSwitchingAMS,
    isSuccess: isSwitchingAMSSuccess,
    isError: isSwitchingAMSError,
    mutate: switchingAMSMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, body }) => apis.rateConnect.switchAMS({ accommodationId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
    },
  })

  const {
    isLoading: isUpdatingBiddingMode,
    isSuccess: isUpdatingBiddingModeSuccess,
    isError: isUpdatingBiddingModeError,
    mutate: updatingBiddingModeMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, body }) => apis.rateConnect.updateBiddingMode({ accommodationId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCStartUp'] })
      queryClient.invalidateQueries({ queryKey: ['RCCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
    },
  })

  const {
    isLoading: isRemovingCampaignMarket,
    isSuccess: isRemovingCampaignMarketSuccess,
    isError: isRemovingCampaignMarketError,
    mutate: removeCampaignMarketMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, marketCode }) => apis.rateConnect.removeMarket({ accommodationId, marketCode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
    },
  })

  const {
    isLoading: isAddingCampaignMarket,
    isSuccess: isAddingCampaignMarketSuccess,
    isError: isAddingCampaignMarketError,
    mutate: addCampaignMarketMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, marketCode }) => apis.rateConnect.addMarket({ accommodationId, marketCode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
    },
  })

  const updateCampaignType = guardSupportMutation(updateCampaignTypeMutation)
  const updateCommissionLevel = guardSupportMutation(updateCommissionLevelMutation)
  const pauseCampaign = guardSupportMutation(pauseCampaignMutation)
  const deactivateCampaign = guardSupportMutation(deactivateCampaignMutation)
  const updateCampaignBudget = guardSupportMutation(updateCampaignBudgetMutation)
  const resumeCampaign = guardSupportMutation(resumeCampaignMutation)
  const switchingAMS = guardSupportMutation(switchingAMSMutation)
  const updatingBiddingMode = guardSupportMutation(updatingBiddingModeMutation)
  const removeCampaignMarket = guardSupportMutation(removeCampaignMarketMutation)
  const addCampaignMarket = guardSupportMutation(addCampaignMarketMutation)

  return {
    isUpdatingCampaignBudget,
    isUpdatingCampaignBudgetSuccess,
    isUpdatingCampaignBudgetError,
    updateCampaignBudget,
    isUpdatingCampaignType,
    isUpdatingCampaignTypeSuccess,
    isUpdatingCampaignTypeError,
    updateCampaignType,
    isResumeCampaignLoading,
    isResumeCampaignSuccess,
    isResumeCampaignError,
    resumeCampaign,
    isUpdatingCommissionLevel,
    isUpdatingCommissionLevelSuccess,
    isUpdatingCommissionLevelError,
    updateCommissionLevel,
    isPausingCampaign,
    isPausingCampaignSuccess,
    isPausingCampaignError,
    pauseCampaign,
    isDeactivatingCampaign,
    isDeactivatingCampaignSuccess,
    isDeactivatingCampaignError,
    deactivateCampaign,
    isRemovingCampaignMarket,
    isRemovingCampaignMarketSuccess,
    isRemovingCampaignMarketError,
    removeCampaignMarket,
    isAddingCampaignMarket,
    isAddingCampaignMarketSuccess,
    isAddingCampaignMarketError,
    addCampaignMarket,
    isSwitchingAMS,
    isSwitchingAMSSuccess,
    isSwitchingAMSError,
    switchingAMS,
    isUpdatingBiddingMode,
    isUpdatingBiddingModeSuccess,
    isUpdatingBiddingModeError,
    updatingBiddingMode,
  }
}

export function usePartnersMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const guardSupportMutation =
    mutate =>
    (...args) => {
      if (userRole.value === 'support') return

      mutate(...args)
    }
  const {
    isLoading: isChangingPartner,
    isSuccess: isChangingPartnerSuccess,
    isError: isChangingPartnerError,
    mutate: changePartnerMutation,
  } = useMutation({
    mutationFn: ({ itemId, partnerId }) => apis.rateConnect.updatePartner({ itemId, partnerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['RCMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCActiveMarkets'] })
      queryClient.invalidateQueries({ queryKey: ['RCCampaigns'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['RCPartners'] })
    },
  })

  const changePartner = guardSupportMutation(changePartnerMutation)

  return {
    isChangingPartner,
    isChangingPartnerSuccess,
    isChangingPartnerError,
    changePartner,
  }
}

export function useBoostLevels() {
  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['BoostLevels'],
    queryFn: () => apis.smartBoost.getBoostLevels(),
  })

  const boostLevels = computed(() => data?.value?.data)
  return { isLoading, boostLevels, isFetching, isSuccess, isError }
}

export function useActiveBoosts(accommodationId) {
  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['ActiveBoosts', accommodationId],
    enabled: computed(() => !!unref(accommodationId)),
    queryFn: ({ queryKey }) => apis.smartBoost.getActiveBoosts({ accommodationId: queryKey[1] }),
  })

  const activeBoosts = computed(() => data?.value?.data)
  return {
    isActiveBoostsLoading: isLoading,
    activeBoosts,
    isActiveBoostsFetching: isFetching,
    isActiveBoostsSuccess: isSuccess,
    isActiveBoostsError: isError,
  }
}

export function useBoostsHistory(accommodationId, selectedCommissionFilter = '') {
  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['BoostsHistory', accommodationId, selectedCommissionFilter],
    enabled: computed(() => !!unref(accommodationId)),
    queryFn: ({ queryKey }) =>
      apis.smartBoost.getBoostsHistory({ accommodationId: queryKey[1], commission: queryKey[2] }),
  })

  const boostsHistory = computed(() => data?.value?.data)
  return {
    isBoostsHistoryLoading: isLoading,
    boostsHistory,
    isBoostsHistoryFetching: isFetching,
    isBoostsHistorySuccess: isSuccess,
    isBoostsHistoryError: isError,
  }
}

export function useAddBoostMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const guardSupportAsyncMutation =
    mutate =>
    async (...args) => {
      if (userRole.value === 'support') return

      return mutate(...args)
    }
  const {
    isLoading: isAddBoostLoading,
    isSuccess: isAddBoostSuccess,
    isError: isAddBoostError,
    mutateAsync: addBoostMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, body }) => apis.smartBoost.addNewBoost({ accommodationId, body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['BoostsHistory'] })
      await queryClient.invalidateQueries({ queryKey: ['ActiveBoosts'] })
      await queryClient.invalidateQueries({ queryKey: ['RCBoostOpportunities'] })
    },
  })

  const addBoost = guardSupportAsyncMutation(addBoostMutation)

  return {
    isAddBoostLoading,
    isAddBoostSuccess,
    isAddBoostError,
    addBoost,
  }
}

export function useEditBoostMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const guardSupportMutation =
    mutate =>
    (...args) => {
      if (userRole.value === 'support') return

      mutate(...args)
    }
  const {
    isLoading: isEditBoostLoading,
    isSuccess: isEditBoostSuccess,
    isError: isEditBoostError,
    mutate: editBoostMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, boostId, body }) => apis.smartBoost.updateBoost({ accommodationId, boostId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['BoostsHistory'] })
      queryClient.invalidateQueries({ queryKey: ['ActiveBoosts'] })
    },
  })

  const editBoost = guardSupportMutation(editBoostMutation)

  return {
    isEditBoostLoading,
    isEditBoostSuccess,
    isEditBoostError,
    editBoost,
  }
}

export function useDeleteBoostMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const guardSupportMutation =
    mutate =>
    (...args) => {
      if (userRole.value === 'support') return

      mutate(...args)
    }
  const {
    isLoading: isDeletingBoostLoading,
    isSuccess: isDeletingBoostSuccess,
    isError: isDeletingBoostError,
    mutate: deleteBoostMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, boostId }) => apis.smartBoost.removeBoost({ accommodationId, boostId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['BoostsHistory'] }),
        queryClient.invalidateQueries({ queryKey: ['ActiveBoosts'] }),
        queryClient.invalidateQueries({ queryKey: ['RCBoostOpportunities'] }),
      ])
    },
  })

  const deleteBoost = guardSupportMutation(deleteBoostMutation)

  return {
    isDeletingBoostLoading,
    isDeletingBoostSuccess,
    isDeletingBoostError,
    deleteBoost,
  }
}
